/** 节点事件类型 */
export type NodeEventType = 'dataChange' | 'error' | 'success' | 'processing' | 'initialized';

/** 节点状态类型 */
export type NodeState = 'dataChange' | 'success' | 'error' | 'processing';

/**
 * 节点数据结构
 */
export interface NodeData<T = unknown> {
  /** 业务数据 */
  value: T;
  /** 错误信息 */
  error?: Error;
  /** 节点状态 */
  state: NodeState;
  /** 更新时间戳 */
  updateTime: number;
  /** 自定义扩展属性 */
  [key: string]: any;
}

/**
 * 节点事件定义
 */
export interface NodeEvent<T = unknown> extends NodeData<T> {
  /** 事件类型 */
  type: NodeEventType;
  /** 触发本次事件的输入端口 ID（可选） */
  triggeredByPort?: string;
  /** 推荐的输出端口映射（dataChange/success 事件专用） */
  outputPortActions?: {
    /** 数据端口：只发送数据，不触发下游执行 */
    dataOnly?: string[];
    /** 流控端口：触发下游执行 */
    triggerFlow?: string[];
  };
}

/**
 * 端口基础定义
 */
export interface BasePort {
  /** 端口唯一标识 */
  id: string;
  /** 显示名称 */
  name: string;
  /** 说明文案（可选） */
  description?: string;
}

/** 输入端口：声明可接受的数据/连接类型 */
export interface InputPort extends BasePort {
  /** 可接受的数据类型（例如：['json','string','number']） */
  acceptTypes?: readonly string[];
  /** 允许连接的数据类型（别名，等价于 acceptTypes） */
  allowTypes?: readonly string[];
  /** 输入数据类型标识（可选） */
  dataType?: string;
}

/** 输出端口：声明输出数据类型或流控端口（例如 dataType==='flow'） */
export interface OutputPort extends BasePort {
  /** 输出数据类型（例如：'json' | 'string' | 'flow'） */
  dataType?: string;
}


/**
 * 节点配置基础接口
 */
export interface NodeConfig {
  /** 节点标识 */
  nodeId?: string;
  /** 节点类型 */
  nodeType?: string;
  /** 自定义配置属性 */
  [key: string]: any;
}

/**
 * 事件处理函数类型
 */
 type EventHandler<T = unknown> = (event: NodeEvent<T>) => void;

/**
 * 节点基类
 * 提供统一的节点生命周期管理、事件机制和数据流处理
 */
export abstract class BaseNode<TConfig extends NodeConfig = NodeConfig, TData = unknown> {
  /** 节点类型标识 */
  static readonly NodeType: string = '基础节点';

  /** 输入端口（推荐使用） */
  protected inputPorts: InputPort[] = [];
  
  /** 输出端口（推荐使用） */
  protected outputPorts: OutputPort[] = [];
  
  /** 节点数据 */
  protected readonly data: NodeData<TData>;
  
  /** 事件订阅者映射 */
  private readonly eventSubscribers = new Map<NodeEventType, Set<EventHandler<TData>>>();

  /** 最后触发的输入端口（用于上下文传递） */
  private lastTriggeredPort?: string;

  /**
   * 构造函数
   * @param settings 节点配置
   * @param uuid 节点唯一标识
   * @param nodeType 节点类型
   * @param label 节点标签
   */
  constructor(
    public readonly settings: TConfig = {} as TConfig,
    public readonly uuid: string,
    public readonly nodeType: string,
    public readonly label: string
  ) {
    this.data = {
      value: undefined as TData,
      state: 'processing',
      updateTime: Date.now()
    };

    // 异步初始化节点
    this.initializeAsync();
  }

  // ==================== 公共 API ====================

  /** 获取节点当前值 */
  public getValue(): TData {
    return this.data.value;
  }

  /** 获取节点状态 */
  public getState(): NodeState {
    return this.data.state;
  }

  /** 获取错误信息 */
  public getError(): Error | undefined {
    return this.data.error;
  }

  /** 获取输入端口（推荐 API） */
  public getInputPorts(): readonly InputPort[] {
    return [...this.inputPorts];
  }

  /** 获取输出端口（推荐 API） */
  public getOutputPorts(): readonly OutputPort[] {
    return [...this.outputPorts];
  }


  /**
   * 更新节点配置
   * @param updates 部分配置更新
   */
  public async updateSettings(updates: Partial<TConfig>): Promise<void> {
    try {
      Object.assign(this.settings, updates);
      
      if (this.checkConfig && !this.checkConfig()) {
        console.warn('节点配置验证失败:', this.nodeType);
        return;
      }
    } catch (error) {
      await this.handleError(error, '配置更新失败');
      throw error;
    }
  }

  /**
   * 接收外部数据
   * @param pointId 输入点ID
   * @param data 输入数据
   */
  public async sendData(pointId: string, data: unknown): Promise<void> {
    try {
      this.setState('processing');
      this.lastTriggeredPort = pointId;
      
      if (!this.receiveData) {
        throw new Error(`节点 ${this.nodeType} 未实现 receiveData 方法`);
      }

      const inputPoint = this.inputPorts.find(p => p.id === pointId);
      if (!inputPoint) {
        throw new Error(`未找到输入点: ${pointId}`);
      }

      await this.receiveData(pointId, data);
    } catch (error) {
      await this.handleError(error, '数据接收失败');
      throw error;
    }
  }

  /**
   * 执行配置更新
   */
  public async executeConfigUpdate(): Promise<void> {
    try {
      this.setState('processing');
      const result = await this.processConfigUpdate(this.settings);
      
      if (result !== undefined && result !== null) {
        if (this.isNodeData(result)) {
          await this.applyNodeData(result);
        } else {
          await this.setSuccess(result as TData);
        }
      }
    } catch (error) {
      await this.handleError(error, '配置处理失败');
      throw error;
    }
  }

  /**
   * 重置节点状态
   */
  public async reset(): Promise<void> {
    this.data.value = undefined as TData;
    this.data.error = undefined;
    this.data.updateTime = Date.now();
    this.lastTriggeredPort = undefined;
    
    this.setState('processing');
    await this.emitEvent('initialized');
  }

  /**
   * 销毁节点，清理资源
   */
  public destroy(): void {
    this.eventSubscribers.clear();
  }

  /**
   * 订阅节点事件
   * @param type 事件类型
   * @param handler 事件处理器
   * @returns 取消订阅函数
   */
  public subscribe(type: NodeEventType, handler: EventHandler<TData>): () => void {
    const handlers = this.eventSubscribers.get(type) ?? new Set<EventHandler<TData>>();
    handlers.add(handler);
    this.eventSubscribers.set(type, handlers);
    
    return () => this.unsubscribe(type, handler);
  }

  /**
   * 取消事件订阅
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public unsubscribe(type: NodeEventType, handler: EventHandler<TData>): void {
    this.eventSubscribers.get(type)?.delete(handler);
  }

  /** 事件订阅别名：与 subscribe 等价 */
  public on(type: NodeEventType, handler: EventHandler<TData>): () => void {
    return this.subscribe(type, handler);
  }

  /** 一次性事件订阅：回调执行一次后自动取消订阅 */
  public once(type: NodeEventType, handler: EventHandler<TData>): () => void {
    let off: () => void = () => {};
    const wrapper: EventHandler<TData> = (e) => {
      try {
        handler(e);
      } finally {
        off();
      }
    };
    off = this.subscribe(type, wrapper);
    return off;
  }

  /** 主动进入 processing 并广播 processing 事件（便于 UI 显示加载态） */
  public async startProcessing(): Promise<void> {
    this.setState('processing');
    await this.emitEvent('processing');
  }

  /**
   * 直接设置节点值
   * @param value 新值
   * @param options.asSuccess 是否在数据变更后将节点标记为 success 并额外触发 success 事件
   */
  public async setValue(value: TData, options?: { asSuccess?: boolean }): Promise<void> {
    const asSuccess = options?.asSuccess ?? false;
    // 优先触发 dataChange，确保容器可基于 outputPortActions 进行端口分发
    await this.updateData('dataChange', value);
    if (asSuccess) {
      this.setState('success');
      await this.emitEvent('success');
    }
  }

  // ==================== 受保护方法 ====================

  /** 设置数据变更状态 */
  protected async setDataChange(value: TData): Promise<void> {
    await this.updateData('dataChange', value);
  }

  /**
   * 子类可重写：决定数据变化时的输出端口行为
   * @param triggeredByPort 触发本次数据变化的输入端口
   * @returns 输出端口行为配置
   */
  protected getOutputPortActions(triggeredByPort?: string): {
    dataOnly?: string[];
    triggerFlow?: string[];
  } {
    // 标记入参使用以满足 noUnusedParameters 要求
    void triggeredByPort;
    // 默认行为：将所有输出端口按类型分类
    const dataOnly: string[] = [];
    const triggerFlow: string[] = [];
    
    this.outputPorts.forEach(point => {
      if (point.dataType === 'flow') {
        triggerFlow.push(point.id);
      } else {
        dataOnly.push(point.id);
      }
    });
    
    return { dataOnly, triggerFlow };
  }

  /** 新增/设置/查询端口与触发端口（推荐 API） */
  protected addInputPort(port: InputPort): void {
    this.inputPorts.push(port);
  }

  protected addOutputPort(port: OutputPort): void {
    this.outputPorts.push(port);
  }

  protected setInputPorts(ports: InputPort[]): void {
    this.inputPorts = [...ports];
  }

  protected setOutputPorts(ports: OutputPort[]): void {
    this.outputPorts = [...ports];
  }

  protected findInputPort(id: string): InputPort | undefined {
    return this.inputPorts.find(p => p.id === id);
  }

  protected findOutputPort(id: string): OutputPort | undefined {
    return this.outputPorts.find(p => p.id === id);
  }

  protected getLastTriggeredPort(): string | undefined {
    return this.lastTriggeredPort;
  }


  // ==================== 私有方法 ====================

  /** 异步初始化节点 */
  private async initializeAsync(): Promise<void> {
    try {
      this.setState('processing');
      await this.initialize?.();
      await this.emitEvent('initialized');
    } catch (error) {
      await this.handleError(error, '节点初始化失败');
    }
  }

  /** 设置成功状态（内部转 dataChange，以确保 outputPortActions 可用） */
  private async setSuccess(value: TData): Promise<void> {
    await this.updateData('dataChange', value);
  }

  /** 设置节点状态（不触发事件） */
  private setState(state: NodeState): void {
    this.data.state = state;
    this.data.updateTime = Date.now();
  }

  /**
   * 更新节点数据并触发事件
   * @param state 新状态
   * @param value 新数据值
   * @param error 错误信息
   */
  private async updateData(state: NodeState, value?: TData, error?: unknown): Promise<void> {
    if (value !== undefined) {
      // 支持对象类型的数据浅合并（当新旧值均为对象时）
      if (typeof value === 'object' && value !== null && typeof this.data.value === 'object' && this.data.value !== null) {
        this.data.value = { ...(this.data.value as any), ...(value as any) } as TData;
      } else {
        this.data.value = value;
      }
    }

    if (error !== undefined) {
      this.data.error = error instanceof Error ? error : new Error(String(error));
    }

    this.data.state = state;
    this.data.updateTime = Date.now();
    
    await this.emitEvent(state);
  }

  /** 将外部提供的 NodeData 写入当前节点数据 */
  private async applyNodeData(external: Partial<NodeData<TData>>): Promise<void> {
    if (external.value !== undefined) {
      this.data.value = external.value;
    }
    if (external.error !== undefined) {
      this.data.error = external.error;
    }
    if (external.state !== undefined) {
      this.data.state = external.state;
    } else {
      this.data.state = 'dataChange';
    }
    this.data.updateTime = Date.now();
    await this.emitEvent('dataChange');
  }

  /** 类型守卫：判断是否为 NodeData */
  private isNodeData(x: unknown): x is NodeData<TData> {
    if (x && typeof x === 'object') {
      const o = x as any;
      return 'state' in o && 'updateTime' in o && ('value' in o || 'error' in o);
    }
    return false;
  }

  /** 统一错误处理 */
  private async handleError(error: unknown, context: string): Promise<void> {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    console.error(`[${this.nodeType}] ${context}:`, errorObj);
    
    await this.updateData('error', undefined, errorObj);
  }

  /** 广播事件 */
  private async emitEvent(type: NodeEventType): Promise<void> {
    const event: NodeEvent<TData> = {
      type,
      ...this.data,
      triggeredByPort: this.lastTriggeredPort
    } as NodeEvent<TData>;

    if (type === 'dataChange' || type === 'success') {
      event.outputPortActions = this.getOutputPortActions(this.lastTriggeredPort);
    }

    const handlers = this.eventSubscribers.get(type);
    if (handlers) {
      const promises = Array.from(handlers).map(handler => {
        try {
          return Promise.resolve(handler(event));
        } catch (e) {
          console.error(`[${this.nodeType}] 事件处理异常(${type}):`, e);
          return Promise.resolve();
        }
      });
      await Promise.allSettled(promises);
    }
  }

  // ==================== 抽象方法（由子类实现） ====================

  /** 子类可实现：初始化逻辑（可异步） */
  protected abstract initialize?(): Promise<void> | void;

  /** 子类可实现：接收输入数据 */
  protected abstract receiveData?(pointId: string, data: unknown): Promise<void> | void;

  /** 子类可实现：配置校验 */
  protected abstract checkConfig?(): boolean;

  /** 子类必须实现：根据配置执行处理，返回结果或 NodeData */
  protected abstract processConfigUpdate(settings: Partial<TConfig>): Promise<TData | NodeData<TData>>;

  /** 仅用于子类便捷读取当前节点数据（受保护） */
  protected get nodeData(): NodeData<TData> {
    return this.data;
  }
}